/**
 * Red Flags Generator
 * Generates warnings and constraints for installation planning
 */

import type { SystemRecInput, RedFlag } from './types';

/**
 * Generate red flags based on system constraints and conditions
 * Includes screed/sealing rules as specified
 */
export function generateRedFlags(input: SystemRecInput): RedFlag[] {
  const flags: RedFlag[] = [];

  // 1) Pipework in screed and not sealed
  if (input.pipeworkInScreed === "yes" && input.systemAlreadySealed !== "yes") {
    flags.push({
      id: "pipework_in_screed_and_not_sealed",
      severity: "warning",
      title: "Pipework in screed",
      detail: "Sealing an old/open system may increase leak risk where pipework is buried.",
      mitigation: "Only seal if system condition and access points allow; assess carefully during survey."
    });
  }

  // 2) Pipework in screed but already sealed
  if (input.pipeworkInScreed === "yes" && input.systemAlreadySealed === "yes") {
    flags.push({
      id: "pipework_in_screed_but_already_sealed",
      severity: "info",
      title: "Pipework in screed (already sealed)",
      detail: "System is already sealed; avoid unnecessary changes that increase pressure risk.",
      mitigation: "Focus on water quality, controls, and flow optimisation."
    });
  }

  // 3) Low mains pressure
  if (input.mainsPressure && input.mainsPressure < 1.5) {
    flags.push({
      id: "low_mains_pressure",
      severity: "warning",
      title: "Low mains pressure",
      detail: "Some stored hot water options may not perform as intended.",
      mitigation: "Confirm static and working pressure; consider pressure solutions or alternative HW approach."
    });
  }

  // 4) High hot water demand vs combi
  if (input.hotWaterDemand === "high" && 
      (input.currentBoiler === "combi" || input.newABoiler === "combi" || input.newBBoiler === "combi")) {
    flags.push({
      id: "hot_water_high_demand_vs_combi",
      severity: "info",
      title: "High hot water demand",
      detail: "On-demand hot water may struggle with simultaneous outlets.",
      mitigation: "Stored hot water may provide more consistent multi-outlet performance."
    });
  }

  // 5) Distribution constraint (cold radiators + small primaries)
  const hasColdRads = input.contaminationSymptoms?.radiator_cold_bottom || 
                      input.contaminationSymptoms?.slow_warmup ||
                      input.emitters?.roomsStruggleToHeat === "yes";
  
  const hasSmallPrimaries = input.emitters?.pipeBore === "22mm" || 
                            input.emitters?.pipeBore === "15mm" ||
                            input.emitters?.pipeBore === "unknown";
  
  const hasHighHeatLoad = (input.heatLossKw && input.heatLossKw >= 19) ||
                          (input.knownHeatLossKw && input.knownHeatLossKw >= 19);

  if (hasColdRads && hasSmallPrimaries && hasHighHeatLoad) {
    flags.push({
      id: "distribution_constraint",
      severity: "warning",
      title: "Distribution may be limiting performance",
      detail: "Symptoms suggest heat is not moving effectively through the system.",
      mitigation: "Check boiler sizing, balance, layout (Y→S), and primary sizing (22→28 where appropriate)."
    });
  }

  // 6) Y-Plan with high heat load (> 18kW) - aligned with fitScoring threshold
  // Y-Plan becomes a weakness at higher heat loads where S-Plan is recommended
  const systemLayout = input.distribution?.systemLayout;
  const heatLoss = input.heatLossKw || input.knownHeatLossKw || 0;
  if (systemLayout === 'y_plan' && heatLoss > 18) {
    flags.push({
      id: "y_plan_high_heat_load",
      severity: "warning",
      title: "Y-Plan not optimal for heat load",
      detail: "Above 18kW, Y-Plan becomes a limitation. S-Plan provides better performance with independent heating and hot water control.",
      mitigation: "Consider S-Plan configuration for improved zoning and efficiency at this heat load."
    });
  }

  return flags;
}
